import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProperties, updateProperty, rotateToken, getQRCode } from '../lib/api';
import { BACKEND_URL } from '../lib/config';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { QRCodeSVG } from 'qrcode.react';
import MobileNav from '../components/MobileNav';
import { ArrowLeft, Download, Copy, RotateCw, MapPin } from 'lucide-react';
import { motion } from 'framer-motion';

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRotateDialog, setShowRotateDialog] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [qrImageUrl, setQrImageUrl] = useState('');

  const loadProperty = async () => {
    try {
      const response = await getProperties();
      const prop = response.data.properties.find((p) => p.id === id);
      if (prop) {
        setProperty(prop);
        setName(prop.name);
        setAddress(prop.address || '');
        setIsActive(prop.isActive);
        const frontendUrl = BACKEND_URL;
        const url = `${frontendUrl}/c/${prop.slug}?t=${prop.token}`;
        setQrUrl(url);
        // Load the branded QR (GuestCheckout logo in the center) for the preview
        try {
          const qrResp = await getQRCode(prop.id);
          setQrImageUrl(URL.createObjectURL(new Blob([qrResp.data])));
        } catch (e) {
          setQrImageUrl('');
        }
      } else {
        toast.error('Property not found');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to load property');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProperty();
  }, [id]);

  useEffect(() => {
    return () => { if (qrImageUrl) URL.revokeObjectURL(qrImageUrl); };
  }, [qrImageUrl]);

  const handleToggleActive = async (val) => {
    setSaving(true);
    setIsActive(val);
    try {
      await updateProperty(id, { name, address, isActive: val });
      toast.success(val ? 'Property activated' : 'Property deactivated');
      loadProperty();
    } catch (error) {
      setIsActive(!val);
      toast.error('Failed to update property');
    } finally {
      setSaving(false);
    }
  };

  const handleRotateToken = async () => {
    try {
      await rotateToken(id);
      toast.success('Token rotated successfully. QR code updated.');
      setShowRotateDialog(false);
      loadProperty();
    } catch (error) {
      toast.error('Failed to rotate token');
    }
  };

  const handleDownloadQR = async () => {
    try {
      const response = await getQRCode(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${property.slug}-qr.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('QR code downloaded');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(qrUrl);
    toast.success('URL copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!property) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      <MobileNav />

      <main className="flex-1 mobile-container pb-28 md:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <button 
            onClick={() => navigate('/dashboard')} 
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            Back to Properties
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-2">{property.name}</h1>
          {property.address && (
            <p className="text-slate-600 flex items-center gap-2">
              <MapPin size={16} />
              {property.address}
            </p>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - QR Code */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-3d p-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-6">QR Code</h2>
            <div className="qr-container-3d mx-auto mb-6" data-testid="qr-code-display">
              {qrImageUrl ? (
                <img src={qrImageUrl} alt="QR code with GuestCheckout logo" width={200} height={200} className="rounded-lg" data-testid="qr-code-image" />
              ) : (
                <QRCodeSVG value={qrUrl} size={200} level="H" />
              )}
            </div>
            <div className="space-y-3">
              <button 
                onClick={handleDownloadQR} 
                className="w-full h-14 rounded-2xl font-semibold text-white flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-emerald-500 hover:opacity-90 transition-opacity"
                data-testid="download-qr-button"
              >
                <Download size={20} />
                Download QR Code
              </button>
              <button 
                onClick={handleCopyUrl} 
                className="w-full h-14 rounded-2xl font-semibold flex items-center justify-center gap-2 transition-transform hover:scale-[0.99]"
                style={{ background: 'linear-gradient(#fff,#fff) padding-box, linear-gradient(135deg,#3B82F6,#10B981) border-box', border: '2px solid transparent', color: '#0f766e' }}
                data-testid="copy-url-button"
              >
                <Copy size={20} />
                Copy Checkout URL
              </button>
              {/* Visible checkout URL — so admins understand what the QR points to */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3" data-testid="checkout-url-box">
                <p className="text-xs font-semibold text-slate-500 mb-1.5">Add the URL to guest messages. It works just like the QR code.</p>
                <code className="block w-full break-all text-sm text-slate-800 font-mono bg-white border border-slate-200 rounded-lg px-3 py-2" title={qrUrl} data-testid="checkout-url-text">
                  {qrUrl}
                </code>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Settings */}
          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card-3d p-6"
            >
              <h2 className="text-xl font-bold text-slate-900 mb-4">Property Status</h2>
              <div className="flex items-center justify-between py-3.5 px-4 bg-slate-50 rounded-2xl border border-slate-100">
                <div className="flex flex-col gap-1">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border w-fit ${
                      isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-600 border-red-200'
                    }`}
                    data-testid="property-active-badge"
                  >
                    <span className="relative flex h-2 w-2">
                      <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isActive ? 'bg-green-400' : 'bg-red-400'}`}></span>
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
                    </span>
                    {isActive ? 'Active' : 'Inactive'}
                  </span>
                  <p className="text-xs text-slate-500">{isActive ? 'QR checkout is live' : 'QR checkout is disabled'}</p>
                </div>
                <Switch
                  id="active"
                  checked={isActive}
                  onCheckedChange={handleToggleActive}
                  disabled={saving}
                  data-testid="active-switch"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card-3d p-6"
            >
              <h2 className="text-xl font-bold text-slate-900 mb-4">Security</h2>
              <p className="text-sm text-slate-600 mb-4">
                Rotating the token will generate a new QR code. The old QR code will stop working immediately.
              </p>
              <button
                onClick={() => setShowRotateDialog(true)}
                className="btn-3d w-full h-12 rounded-xl font-semibold bg-white border-2 border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center gap-2"
                data-testid="rotate-token-button"
              >
                <RotateCw size={18} />
                Rotate Token
              </button>
            </motion.div>
          </div>
        </div>
      </main>

      <AlertDialog open={showRotateDialog} onOpenChange={setShowRotateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate Token</AlertDialogTitle>
            <AlertDialogDescription>
              This will generate a new token and QR code. The old QR code will immediately stop
              working. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-rotate">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRotateToken} data-testid="confirm-rotate">
              Rotate Token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
